import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import type {
  DailyGoals_QueryDailyGoalsQuery as QueryDailyGoals,
  DailyGoals_QueryDailyGoalsQueryVariables as QueryDailyGoalsQueryVariables,
} from 'bundles/daily-goals/__queries__/__generated__/queryDailyGoals';
import queryDailyGoals from 'bundles/daily-goals/__queries__/queryDailyGoals.graphql';
import { useIsDailyGoalsEnabled } from 'bundles/daily-goals/utils/epicFeatureFlags';

const useQueryDailyGoals = (activeCourseId?: string) => {
  const { isDailyGoalsFeatureEnabled } = useIsDailyGoalsEnabled();
  const { data, loading, error } = useQuery<QueryDailyGoals, QueryDailyGoalsQueryVariables>(queryDailyGoals, {
    variables: { activeCourseId: activeCourseId ?? '' },
    // Ensure we never run this query if the feature flag is disabled. Allows us to place DailyGoalsDataProvider in the tree arbitrarily without adding an epic check:
    skip: !activeCourseId || !isDailyGoalsFeatureEnabled,
    context: { clientName: 'gatewayGql' },
  });

  if (error) {
    Sentry.captureException(error, {
      extra: {
        message: 'Daily Goals: Error fetching daily goals',
        courseId: activeCourseId,
      },
    });
  }

  const dailyGoals = data?.DailyGoals?.queryDailyGoals?.dailyGoals;
  const dayOfLearning = data?.DailyGoals?.queryDailyGoals?.dayOfLearning;
  return { dailyGoals, dayOfLearning, loading, error };
};

export default useQueryDailyGoals;
